import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { adminResetUserPasskeys } from "@/lib/passkey";

const ROLES = ["employee", "manager", "hr_user", "admin", "super_admin"];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "employee", temporary_password: "", linked_employee_id: "" });
  const [resetOpen, setResetOpen] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [resetPwd, setResetPwd] = useState("");

  const load = async () => {
    try {
      const u = await api.get("/users"); setUsers(u.data);
      const e = await api.get("/employees"); setEmployees(e.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const payload = { ...form };
      if (!payload.linked_employee_id) delete payload.linked_employee_id;
      await api.post("/users", payload);
      toast.success("User created");
      setOpenNew(false);
      setForm({ email: "", full_name: "", role: "employee", temporary_password: "", linked_employee_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/users/${id}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const updateRole = async (id, role) => {
    try { await api.patch(`/users/${id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resetMfa = async (id) => {
    try { await api.post(`/users/${id}/reset-mfa`); toast.success("MFA reset"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPwdGo = async () => {
    try { await api.post(`/users/${resetFor.id}/reset-password`, { temporary_password: resetPwd }); toast.success("Password reset. User must change on next login."); setResetOpen(false); setResetPwd(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPasskeys = async (u) => {
    if (!window.confirm(`Reset all passkeys for ${u.full_name}? They will need to re-enroll using their password.`)) return;
    try {
      const res = await adminResetUserPasskeys(u.id);
      toast.success(`Removed ${res.removed} passkey(s) for ${res.user_email}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tiny-label">Access management</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">User accounts</h1>
          <p className="text-muted-foreground mt-1">Create users, change roles, reset passwords and MFA.</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild><Button data-testid="new-user-btn"><Plus className="w-4 h-4 mr-1" /> New user</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create user account</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="nu-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="nu-email" /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="nu-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Linked employee (optional)</Label>
                <Select value={form.linked_employee_id || "none"} onValueChange={(v) => setForm({ ...form, linked_employee_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} · {e.employee_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Temporary password</Label><Input type="text" value={form.temporary_password} onChange={(e) => setForm({ ...form, temporary_password: e.target.value })} data-testid="nu-temp-pwd" /></div>
              <div className="text-xs text-muted-foreground">User will be required to change password on first login.</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenNew(false)}>Cancel</Button>
              <Button onClick={create} data-testid="nu-create">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border shadow-none">
        <div className="overflow-x-auto app-scroll">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left tiny-label">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">MFA</th>
                <th className="px-4 py-3">Last login</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium">{u.full_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                      <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={u.status === "active" ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-muted-foreground"}>{u.status}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    {u.mfa_enabled
                      ? <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">{u.mfa_method}</Badge>
                      : <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">off</Badge>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setResetFor(u); setResetOpen(true); }} data-testid={`reset-pwd-${u.email}`}>Reset pwd</Button>
                      <Button size="sm" variant="outline" onClick={() => resetMfa(u.id)}>Reset MFA</Button>
                      <Button size="sm" variant="outline" onClick={() => resetPasskeys(u)} data-testid={`reset-passkeys-${u.email}`}>Reset passkeys</Button>
                      {u.status === "active"
                        ? <Button size="sm" variant="outline" onClick={() => updateStatus(u.id, "disabled")}>Disable</Button>
                        : <Button size="sm" onClick={() => updateStatus(u.id, "active")}>Enable</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set temporary password for {resetFor?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Temporary password</Label>
            <Input type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} data-testid="reset-pwd-input" />
            <div className="text-xs text-muted-foreground">User will be logged out and forced to change password on next login.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button onClick={resetPwdGo} data-testid="reset-pwd-save">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
